import { metinOku, sayiOku, sayiOkuBos } from '../rapor-ekrani/bicim';

/**
 * S359: cari hareketinin belge detayı (`belge` ds'i, parametre `@FIS`). Satır = fişin ürün satırı,
 * başlık alanları her satırda tekrar eder; ürün satırı olmayan fişte (dekont) TEK satır gelir, ürün
 * alanları boştur. Kolon adları VERİ SÖZLEŞMESİDİR (SQL 188).
 */
export interface HamBelgeSatiri {
  fis: string | number | null;
  belge_no: string | null;
  fis_turu: string | null;
  tarih: string | null;
  vade: string | null;
  cari_kodu: string | null;
  cari: string | null;
  lokasyon: string | null;
  aciklama: string | null;
  doviz: string | null;
  satir_toplam: number | string | null;
  iskonto_toplam: number | string | null;
  kdv_toplam: number | string | null;
  otv_toplam: number | string | null;
  tevkifat_toplam: number | string | null;
  yuvarlama: number | string | null;
  genel_toplam: number | string | null;
  /** Ürün satırının kimliği; satırsız fişte null. */
  satir: string | number | null;
  stok_kodu: string | null;
  urun: string | null;
  miktar: number | string | null;
  birim: string | null;
  fiyat: number | string | null;
  /** ERP'nin iskonto metni ("10+13.22+3"); iskontosuz satırda null. */
  iskonto: string | null;
  tutar: number | string | null;
  kdv_orani: number | string | null;
}

export interface BelgeKalemi {
  kimlik: string;
  stokKodu: string;
  urun: string;
  miktar: number;
  birim: string;
  fiyat: number;
  /** Ekran metni ("%10 + %13,22 + %3"); iskontosuzsa ''. */
  iskonto: string;
  /** İskontosuz, KDV hariç satır tutarı (ERP'nin satır TUTAR'ı). */
  tutar: number;
}

/** Toplam bloğunun bir satırı. ÖTV, tevkifat ve yuvarlama yalnız doluysa gelir. */
export interface BelgeToplami {
  etiket: string;
  deger: number;
  vurgu?: boolean;
}

export interface BelgeDetayi {
  belgeNo: string;
  tur: string;
  tarih: string | null;
  vade: string | null;
  cariKodu: string;
  cari: string;
  lokasyon: string;
  aciklama: string;
  /** Fiş dövizi TL değilse kodu (tutarlar fişin dövizindedir); TL ise ''. */
  doviz: string;
  kalemler: BelgeKalemi[];
  toplamlar: BelgeToplami[];
  genelToplam: number;
}

const ISKONTO_SAYI = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 });

/** "10+13.22+3" -> "%10 + %13,22 + %3". Sayı olmayan parça olduğu gibi yazılır. */
export function iskontoMetni(ham: string | null | undefined): string {
  const m = metinOku(ham);
  if (m === '' || m === '0') return '';
  return m
    .split('+')
    .map((p) => p.trim())
    .filter((p) => p !== '')
    .map((p) => {
      const n = Number(p);
      return Number.isFinite(n) ? `%${ISKONTO_SAYI.format(n)}` : p;
    })
    .join(' + ');
}

const TL = new Set(['', 'TL', 'TRY', 'YTL']);

/** Ds satırlarından belge. Satır yoksa null (fiş aynada bulunamadı). */
export function belgeDetayi(ham: readonly HamBelgeSatiri[]): BelgeDetayi | null {
  if (ham.length === 0) return null;
  const b = ham[0];
  const kalemler: BelgeKalemi[] = ham
    .filter((s) => metinOku(s.satir) !== '')
    .map((s) => ({
      kimlik: metinOku(s.satir),
      stokKodu: metinOku(s.stok_kodu),
      urun: metinOku(s.urun),
      miktar: sayiOku(s.miktar),
      birim: metinOku(s.birim),
      fiyat: sayiOku(s.fiyat),
      iskonto: iskontoMetni(s.iskonto),
      tutar: sayiOku(s.tutar),
    }));

  const genelToplam = sayiOku(b.genel_toplam);
  // ERP "Genel Toplam" sırası (Yerlice belge detayıyla aynı): Tutar, Toplam İskonto, Ara Toplam, KDV, Genel Toplam.
  // Ürün satırı olmayan fişte (dekont) ara kalemler anlamsız: yalnız Genel Toplam.
  const toplamlar: BelgeToplami[] = [];
  if (kalemler.length > 0) {
    const tutar = sayiOku(b.satir_toplam);
    const iskonto = sayiOku(b.iskonto_toplam);
    toplamlar.push(
      { etiket: 'Tutar', deger: tutar },
      { etiket: 'Toplam İskonto', deger: iskonto },
      { etiket: 'Ara Toplam', deger: tutar - iskonto },
      { etiket: 'KDV', deger: sayiOku(b.kdv_toplam) },
    );
    const ek: Array<[string, unknown]> = [['ÖTV', b.otv_toplam], ['Tevkifat', b.tevkifat_toplam], ['Yuvarlama', b.yuvarlama]];
    for (const [etiket, v] of ek) {
      const n = sayiOkuBos(v);
      if (n !== null && n !== 0) toplamlar.push({ etiket, deger: n });
    }
  }
  toplamlar.push({ etiket: 'Genel Toplam', deger: genelToplam, vurgu: true });

  const doviz = metinOku(b.doviz).toUpperCase();
  return {
    belgeNo: metinOku(b.belge_no),
    tur: metinOku(b.fis_turu),
    tarih: b.tarih || null,
    vade: b.vade || null,
    cariKodu: metinOku(b.cari_kodu),
    cari: metinOku(b.cari),
    lokasyon: metinOku(b.lokasyon),
    aciklama: metinOku(b.aciklama),
    doviz: TL.has(doviz) ? '' : doviz,
    kalemler,
    toplamlar,
    genelToplam,
  };
}
